import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LoginPage } from '@/pages/LoginPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ChangePasswordPage } from '@/pages/ChangePasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { HandoffPage } from '@/pages/HandoffPage';
import { NotificationsSettingsPage } from '@/pages/NotificationsSettingsPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { ConsoleShell } from '@/console/ConsoleShell';
import { MyDayPage } from '@/pages/MyDayPage';
import { ConversationsPage } from '@/pages/ConversationsPage';
import { ContactPage } from '@/pages/ContactPage';
import { QueuePage } from '@/pages/QueuePage';
import { TeamPage } from '@/pages/TeamPage';
import { SpamPage } from '@/pages/SpamPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { useLangStore, dirFor } from '@/stores/langStore';
import { SupportChatMount } from '@/support/SupportChatMount';

// Opt into React Router v7 semantics now to silence the future-flag warnings.
const routerFutureFlags = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
} as const;

/**
 * Root application component. The authenticated area renders under a single
 * <ConsoleShell/> (rail + topbar + hub), with the five views as nested routes:
 * My Day (index), Conversations, Live Queue, Team, Settings.
 */
const App: React.FC = () => {
  const lang = useLangStore((s) => s.lang);

  // Reflect the chosen UI language on the document root (RTL for Hebrew). The
  // shell also sets dir on .ac-root; this keeps <html> consistent for portals.
  React.useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dirFor(lang);
  }, [lang]);

  return (
    <ErrorBoundary>
      {/* BotableX's own support chat — signed-in users only, identified; see src/support/supportChat.ts */}
      <SupportChatMount />
      <BrowserRouter future={routerFutureFlags}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* Forced password change — PUBLIC / PRE-session. A temporary-password account is
              refused at /login (403 password_reset_required, no token); LoginPage routes
              here with the credentials in router state. Redirects to /login without it. */}
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Sign-in hand-off from the marketing site — PUBLIC / PRE-session. A single-use code
              in the URL fragment is redeemed for a session. See HandoffPage. */}
          <Route path="/auth/handoff" element={<HandoffPage />} />
          <Route
            element={
              <ProtectedRoute>
                <ConsoleShell />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<MyDayPage />} />
            <Route path="/conversations" element={<ConversationsPage />} />
            {/* Same element as the bare path on purpose: switching conversations changes only the
                param, so the workspace never unmounts and remounts mid-switch. Rail highlighting is
                unaffected — it matches on startsWith('/conversations'). */}
            <Route path="/conversations/:sessionId" element={<ConversationsPage />} />
            <Route path="/contacts/:customerId" element={<ContactPage />} />
            <Route path="/queue" element={<QueuePage />} />
            <Route path="/team" element={<TeamPage />} />
            {/* OP-1005 — conversations marked as spam, with Undo. workspace.handle (checked inside). */}
            <Route path="/spam" element={<SpamPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/notifications" element={<NotificationsSettingsPage />} />
          </Route>
          <Route path="/404" element={<NotFoundPage />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
